import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRotateRight,
  faBox,
  faCheckCircle,
  faClock,
  faEye,
  faMoneyBill,
  faSearch,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";

import {
  toast,
} from "react-hot-toast";

import {
  ApiError,
} from "../api/apiError";

import {
  ordersAdminApi,
} from "../api/ordersAdminApi";

import type {
  AdminOrdersQuery,
  DeliveryStatus,
  InstallationStatus,
  OrderRecord,
  OrderStatus,
  OrdersPagination,
  PaymentStatus,
} from "../types/orders-admin.types";

import "../screenstyles/AdminOrders.css";

function formatDate(
  value: any,
) {
  if (!value) {
    return "—";
  }

  let date:
    Date |
    null = null;

  if (
    typeof value ===
    "string"
  ) {
    date =
      new Date(value);
  } else {
    const seconds =
      value.seconds ??
      value._seconds;

    if (
      typeof seconds ===
      "number"
    ) {
      date =
        new Date(
          seconds *
            1000,
        );
    }
  }

  if (
    !date ||
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year:
        "numeric",

      month:
        "short",

      day:
        "2-digit",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(date);
}

function orderStatusLabel(
  value:
    string,
) {
  const map:
    Record<
      string,
      string
    > = {
      pending:
        "قيد الانتظار",

      confirmed:
        "مؤكد",

      processing:
        "قيد التجهيز",

      completed:
        "مكتمل",

      cancelled:
        "ملغي",

      rejected:
        "مرفوض",
    };

  return (
    map[value] ??
    value
  );
}

function paymentStatusLabel(
  value:
    string,
) {
  const map:
    Record<
      string,
      string
    > = {
      pending:
        "بانتظار الدفع",

      paid:
        "مدفوع",

      completed:
        "مدفوع",

      failed:
        "فشل",

      partially_refunded:
        "استرجاع جزئي",

      refunded:
        "مسترجع",
    };

  return (
    map[value] ??
    value
  );
}

const AdminOrders:
React.FC = () => {
  const navigate =
    useNavigate();

  const [
    orders,
    setOrders,
  ] =
    useState<
      OrderRecord[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    loadingMore,
    setLoadingMore,
  ] =
    useState(false);

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    debouncedSearch,
    setDebouncedSearch,
  ] =
    useState("");

  const [
    status,
    setStatus,
  ] =
    useState<
      OrderStatus |
      ""
    >("");

  const [
    paymentStatus,
    setPaymentStatus,
  ] =
    useState<
      PaymentStatus |
      ""
    >("");

  const [
    deliveryStatus,
    setDeliveryStatus,
  ] =
    useState<
      DeliveryStatus |
      ""
    >("");

  const [
    installationStatus,
    setInstallationStatus,
  ] =
    useState<
      InstallationStatus |
      ""
    >("");

  const [
    sort,
    setSort,
  ] =
    useState<
      NonNullable<
        AdminOrdersQuery[
          "sort"
        ]
      >
    >(
      "createdAt_desc",
    );

  const [
    pagination,
    setPagination,
  ] =
    useState<
      OrdersPagination
    >({
      limit: 50,
      hasMore: false,
      nextCursor: null,
    });

  const [
    total,
    setTotal,
  ] =
    useState(0);

  useEffect(() => {
    const timer =
      window.setTimeout(
        () => {
          setDebouncedSearch(
            search.trim(),
          );
        },
        500,
      );

    return () =>
      window.clearTimeout(
        timer,
      );
  }, [
    search,
  ]);

  const fetchOrders =
    useCallback(
      async (
        options: {
          append?: boolean;
          cursor?: string;
        } = {},
      ) => {
        const {
          append = false,
          cursor,
        } = options;

        try {
          if (
            append
          ) {
            setLoadingMore(
              true,
            );
          } else {
            setLoading(
              true,
            );
          }

          const result =
            await ordersAdminApi
              .list({
                search:
                  debouncedSearch ||
                  undefined,

                status:
                  status ||
                  undefined,

                paymentStatus:
                  paymentStatus ||
                  undefined,

                deliveryStatus:
                  deliveryStatus ||
                  undefined,

                installationStatus:
                  installationStatus ||
                  undefined,

                sort,

                limit: 50,

                cursor,
              });

          if (
            append
          ) {
            setOrders(
              previous => {
                const map =
                  new Map<
                    string,
                    OrderRecord
                  >();

                previous.forEach(
                  order =>
                    map.set(
                      order.orderId,
                      order,
                    ),
                );

                result.orders.forEach(
                  order =>
                    map.set(
                      order.orderId,
                      order,
                    ),
                );

                return Array.from(
                  map.values(),
                );
              },
            );
          } else {
            setOrders(
              result.orders,
            );
          }

          setTotal(
            result.count,
          );

          setPagination(
            result.pagination,
          );
        } catch (
          error
        ) {
          console.error(
            "Failed to load orders:",
            error,
          );

          if (
            error instanceof
            ApiError
          ) {
            if (
              error.status ===
              403
            ) {
              toast.error(
                "لا تمتلك صلاحية عرض الطلبات",
              );

              return;
            }

            toast.error(
              error.message ||
                "فشل تحميل الطلبات",
            );

            return;
          }

          toast.error(
            "تعذر الاتصال بالخادم",
          );
        } finally {
          setLoading(
            false,
          );

          setLoadingMore(
            false,
          );
        }
      },
      [
        debouncedSearch,
        status,
        paymentStatus,
        deliveryStatus,
        installationStatus,
        sort,
      ],
    );

  useEffect(() => {
    fetchOrders();
  }, [
    fetchOrders,
  ]);

  const stats =
    useMemo(
      () => ({
        visible:
          orders.length,

        pending:
          orders.filter(
            order =>
              order.status ===
              "pending",
          ).length,

        completed:
          orders.filter(
            order =>
              order.status ===
              "completed",
          ).length,

        totalValue:
          orders.reduce(
            (
              sum,
              order,
            ) =>
              sum +
              Number(
                order.totalOrderPrice ||
                  0,
              ),
            0,
          ),
      }),
      [
        orders,
      ],
    );

  const clearFilters =
    () => {
      setSearch("");
      setDebouncedSearch("");
      setStatus("");
      setPaymentStatus("");
      setDeliveryStatus("");
      setInstallationStatus("");
      setSort(
        "createdAt_desc",
      );
    };

  return (
    <div className="admin-orders-page">

      <section className="admin-orders-header">
        <div>
          <span>
            إدارة الطلبات
          </span>

          <h1>
            جميع الطلبات
          </h1>

          <p>
            إدارة الطلبات وحالاتها
            والدفع والعناوين والمواد
            ومتابعة سجل التغييرات.
          </p>
        </div>

        <button
          type="button"
          onClick={() =>
            fetchOrders()
          }
          disabled={
            loading
          }
        >
          <FontAwesomeIcon
            icon={
              faArrowRotateRight
            }
          />

          تحديث
        </button>
      </section>

      <section className="admin-orders-stats">
        <div>
          <FontAwesomeIcon
            icon={
              faShoppingCart
            }
          />

          <span>
            إجمالي النتائج
          </span>

          <strong>
            {total}
          </strong>
        </div>

        <div>
          <FontAwesomeIcon
            icon={faClock}
          />

          <span>
            قيد الانتظار
          </span>

          <strong>
            {stats.pending}
          </strong>
        </div>

        <div>
          <FontAwesomeIcon
            icon={
              faCheckCircle
            }
          />

          <span>
            مكتملة
          </span>

          <strong>
            {stats.completed}
          </strong>
        </div>

        <div>
          <FontAwesomeIcon
            icon={
              faMoneyBill
            }
          />

          <span>
            قيمة الصفحة الحالية
          </span>

          <strong>
            {stats.totalValue.toLocaleString(
              "en-US",
            )}
          </strong>
        </div>
      </section>

      <section className="admin-orders-toolbar">

        <div className="admin-orders-search">
          <FontAwesomeIcon
            icon={faSearch}
          />

          <input
            type="text"
            value={
              search
            }
            placeholder="بحث برقم الطلب أو المستخدم..."
            onChange={
              event =>
                setSearch(
                  event
                    .target
                    .value,
                )
            }
          />
        </div>

        <select
          value={
            status
          }
          onChange={
            event =>
              setStatus(
                event
                  .target
                  .value as
                  OrderStatus |
                  "",
              )
          }
        >
          <option value="">
            جميع حالات الطلب
          </option>

          <option value="pending">
            قيد الانتظار
          </option>

          <option value="confirmed">
            مؤكد
          </option>

          <option value="processing">
            قيد التجهيز
          </option>

          <option value="completed">
            مكتمل
          </option>

          <option value="cancelled">
            ملغي
          </option>

          <option value="rejected">
            مرفوض
          </option>
        </select>

        <select
          value={
            paymentStatus
          }
          onChange={
            event =>
              setPaymentStatus(
                event
                  .target
                  .value as
                  PaymentStatus |
                  "",
              )
          }
        >
          <option value="">
            جميع حالات الدفع
          </option>

          <option value="pending">
            بانتظار الدفع
          </option>

          <option value="paid">
            مدفوع
          </option>

          <option value="failed">
            فشل
          </option>

          <option value="partially_refunded">
            استرجاع جزئي
          </option>

          <option value="refunded">
            مسترجع
          </option>
        </select>

        <select
          value={
            deliveryStatus
          }
          onChange={
            event =>
              setDeliveryStatus(
                event
                  .target
                  .value as
                  DeliveryStatus |
                  "",
              )
          }
        >
          <option value="">
            جميع حالات التوصيل
          </option>

          <option value="pending">
            بانتظار التوصيل
          </option>

          <option value="preparing">
            تجهيز
          </option>

          <option value="ready_for_delivery">
            جاهز للتوصيل
          </option>

          <option value="out_for_delivery">
            قيد التوصيل
          </option>

          <option value="delivered">
            تم التوصيل
          </option>

          <option value="failed">
            فشل
          </option>

          <option value="cancelled">
            ملغي
          </option>
        </select>

        <select
          value={
            installationStatus
          }
          onChange={
            event =>
              setInstallationStatus(
                event
                  .target
                  .value as
                  InstallationStatus |
                  "",
              )
          }
        >
          <option value="">
            جميع حالات التنصيب
          </option>

          <option value="pending_assignment">
            بانتظار فني
          </option>

          <option value="assigned">
            تم تعيين الفني
          </option>

          <option value="scheduled">
            مجدول
          </option>

          <option value="in_progress">
            قيد التنفيذ
          </option>

          <option value="completed">
            مكتمل
          </option>

          <option value="cancelled">
            ملغي
          </option>
        </select>

        <select
          value={
            sort
          }
          onChange={
            event =>
              setSort(
                event
                  .target
                  .value as
                  typeof sort,
              )
          }
        >
          <option value="createdAt_desc">
            الأحدث
          </option>

          <option value="createdAt_asc">
            الأقدم
          </option>

          <option value="total_desc">
            الأعلى سعرًا
          </option>

          <option value="total_asc">
            الأقل سعرًا
          </option>
        </select>

        <button
          type="button"
          className="admin-orders-clear-btn"
          onClick={
            clearFilters
          }
        >
          مسح الفلاتر
        </button>

      </section>

      <section className="admin-orders-table-card">

        {loading ? (
          <div className="admin-orders-state">
            جاري تحميل الطلبات...
          </div>
        ) : orders.length ===
          0 ? (
          <div className="admin-orders-state">
            <FontAwesomeIcon
              icon={faBox}
            />

            لا توجد طلبات.
          </div>
        ) : (
          <div className="admin-orders-table-wrapper">
            <table className="admin-orders-table">
              <thead>
                <tr>
                  <th>
                    #
                  </th>

                  <th>
                    رقم الطلب
                  </th>

                  <th>
                    المستخدم
                  </th>

                  <th>
                    حالة الطلب
                  </th>

                  <th>
                    الدفع
                  </th>

                  <th>
                    التوصيل
                  </th>

                  <th>
                    التنصيب
                  </th>

                  <th>
                    المواد
                  </th>

                  <th>
                    الإجمالي
                  </th>

                  <th>
                    التاريخ
                  </th>

                  <th>
                    التفاصيل
                  </th>
                </tr>
              </thead>

              <tbody>
                {orders.map(
                  (
                    order,
                    index,
                  ) => (
                    <tr
                      key={
                        order.orderId
                      }
                    >
                      <td>
                        {index +
                          1}
                      </td>

                      <td dir="ltr">
                        <span className="admin-orders-id">
                          {
                            order.orderId
                          }
                        </span>
                      </td>

                      <td>
                        <div className="admin-orders-user">
                          <strong>
                            {order.userName ||
                              "مستخدم"}
                          </strong>

                          <span>
                            {order.userPhone ||
                              order.userEmail ||
                              order.userId}
                          </span>
                        </div>
                      </td>

                      <td>
                        <span
                          className={`admin-order-status status-${order.status}`}
                        >
                          {orderStatusLabel(
                            order.status,
                          )}
                        </span>
                      </td>

                      <td>
                        <span className="admin-order-payment">
                          {paymentStatusLabel(
                            order.paymentStatus,
                          )}
                        </span>
                      </td>

                      <td>
                        {
                          order.deliveryStatus
                        }
                      </td>

                      <td>
                        {
                          order.installationStatus
                        }
                      </td>

                      <td>
                        {
                          order.totalItems
                        }
                      </td>

                      <td>
                        <strong>
                          {Number(
                            order.totalOrderPrice ||
                              0,
                          ).toLocaleString(
                            "en-US",
                          )}

                          {" "}
                          د.ع
                        </strong>
                      </td>

                      <td>
                        {formatDate(
                          order.createdAt,
                        )}
                      </td>

                      <td>
                        <button
                          type="button"
                          className="admin-orders-view-btn"
                          onClick={() =>
                            navigate(
                              `/orders-admin/${order.orderId}`,
                            )
                          }
                        >
                          <FontAwesomeIcon
                            icon={
                              faEye
                            }
                          />

                          عرض
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}

      </section>

      {pagination.hasMore &&
        pagination.nextCursor && (
          <div className="admin-orders-load-more">
            <button
              type="button"
              disabled={
                loadingMore
              }
              onClick={() =>
                fetchOrders({
                  append:
                    true,

                  cursor:
                    pagination.nextCursor ??
                    undefined,
                })
              }
            >
              {loadingMore
                ? "جاري التحميل..."
                : "تحميل المزيد"}
            </button>
          </div>
        )}
    </div>
  );
};

export default AdminOrders;